import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarCheck, Phone, X } from "lucide-react";
import { toast } from "sonner";
import { BUSINESS } from "../data/content";

const INITIAL = { name: "", phone: "", date: "", time: "", guests: "2", notes: "" };

export const ReservationModal = ({ open, onClose }) => {
  const [form, setForm] = useState(INITIAL);
  const [summary, setSummary] = useState(null);
  const firstFieldRef = useRef(null);
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    const focusTimer = setTimeout(() => firstFieldRef.current?.focus(), 80);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
      clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setSummary(null);
      setForm(INITIAL);
    }
  }, [open]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 8 || !form.date || !form.time) {
      toast.error("Please add your name, a valid phone number, date and time.");
      return;
    }
    setSummary({ ...form });
    toast.success("Enquiry noted — this demo form doesn't send anything. Please call to confirm.");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="reservation-modal"
        >
          <button
            type="button"
            aria-label="Close reservation form"
            data-testid="reservation-backdrop"
            className="absolute inset-0 bg-[#0c0a08]/80 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reservation-modal-title"
            className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-[rgba(247,244,239,0.12)] bg-[#1e1a17] p-7 shadow-[0_16px_40px_rgba(0,0,0,0.5)] sm:rounded-3xl md:p-9"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="reservation-close-button"
              aria-label="Close"
              className="absolute right-5 top-5 rounded-full border border-[rgba(247,244,239,0.15)] p-2 text-[#b5afa7] transition-colors hover:text-[#f7f4ef]"
            >
              <X size={16} />
            </button>

            {!summary ? (
              <>
                <p className="eyebrow mb-3">Table Enquiry</p>
                <h2
                  id="reservation-modal-title"
                  className="font-display text-3xl font-semibold text-[#f7f4ef]"
                >
                  Plan your evening
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#b5afa7]">
                  Demo enquiry form — nothing is sent or booked here. To confirm a table, call{" "}
                  <a
                    href={BUSINESS.phones[0].href}
                    className="font-semibold text-[#d4af37]"
                    data-testid="reservation-modal-phone-link"
                  >
                    {BUSINESS.phones[0].label}
                  </a>
                  .
                </p>
                <form className="mt-7 space-y-5" onSubmit={submit} noValidate={false}>
                  <div>
                    <label htmlFor="res-name" className="field-label">
                      Name
                    </label>
                    <input
                      id="res-name"
                      ref={firstFieldRef}
                      type="text"
                      required
                      autoComplete="name"
                      className="field"
                      placeholder="Your name"
                      value={form.name}
                      onChange={update("name")}
                      data-testid="reservation-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="res-phone" className="field-label">
                      Phone
                    </label>
                    <input
                      id="res-phone"
                      type="tel"
                      required
                      autoComplete="tel"
                      className="field"
                      placeholder="Your phone number"
                      value={form.phone}
                      onChange={update("phone")}
                      data-testid="reservation-phone-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="res-date" className="field-label">
                        Date
                      </label>
                      <input
                        id="res-date"
                        type="date"
                        required
                        min={today}
                        className="field [color-scheme:dark]"
                        value={form.date}
                        onChange={update("date")}
                        data-testid="reservation-date-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="res-time" className="field-label">
                        Time
                      </label>
                      <input
                        id="res-time"
                        type="time"
                        required
                        className="field [color-scheme:dark]"
                        value={form.time}
                        onChange={update("time")}
                        data-testid="reservation-time-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="res-guests" className="field-label">
                      Guests
                    </label>
                    <select
                      id="res-guests"
                      className="field"
                      value={form.guests}
                      onChange={update("guests")}
                      data-testid="reservation-guests-select"
                    >
                      {["1", "2", "3", "4", "5", "6", "7", "8+"].map((n) => (
                        <option key={n} value={n} className="bg-[#1e1a17]">
                          {n} {n === "1" ? "guest" : "guests"}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="res-notes" className="field-label">
                      Special requests
                    </label>
                    <textarea
                      id="res-notes"
                      rows={3}
                      className="field resize-none"
                      placeholder="Occasion, seating preference, anything we should know…"
                      value={form.notes}
                      onChange={update("notes")}
                      data-testid="reservation-notes-textarea"
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn-gold w-full"
                    data-testid="reservation-submit-button"
                  >
                    Review Enquiry
                  </button>
                </form>
              </>
            ) : (
              <div data-testid="reservation-confirmation">
                <div className="mb-5 inline-flex rounded-full border border-[#d4af37]/30 bg-[#d4af37]/10 p-3 text-[#d4af37]">
                  <CalendarCheck size={22} aria-hidden="true" />
                </div>
                <h2
                  id="reservation-modal-title"
                  className="font-display text-3xl font-semibold text-[#f7f4ef]"
                >
                  Your enquiry, {summary.name.split(" ")[0]}
                </h2>
                <dl className="mt-6 space-y-3 text-sm">
                  {[
                    ["Phone", summary.phone],
                    ["Date", summary.date],
                    ["Time", summary.time],
                    ["Guests", summary.guests],
                    ...(summary.notes ? [["Special requests", summary.notes]] : []),
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="flex justify-between gap-6 border-b border-[rgba(247,244,239,0.08)] pb-3"
                    >
                      <dt className="text-[#8f887f]">{k}</dt>
                      <dd className="text-right font-medium text-[#f7f4ef]">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-5 rounded-xl border border-[#d4af37]/25 bg-[#d4af37]/5 p-4 text-sm leading-relaxed text-[#d8d2c9]">
                  This demo form doesn't send your details anywhere. To make the booking, call us
                  and mention these details — we'll take it from there.
                </p>
                <div className="mt-6 space-y-3">
                  <a
                    href={BUSINESS.phones[0].href}
                    className="btn-gold w-full"
                    data-testid="reservation-call-button"
                  >
                    <Phone size={16} aria-hidden="true" /> Call {BUSINESS.phones[0].label}
                  </a>
                  <button
                    type="button"
                    className="btn-outline w-full"
                    data-testid="reservation-again-button"
                    onClick={() => setSummary(null)}
                  >
                    Edit enquiry
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
